import { type ComputedRef, type Ref, computed } from 'vue'
import { type IUseEntityChatParams, type IUseEntityChatReturn, useEntityChat } from '@/modules/chat-matrix-fast/composables/useEntityChat'
import { type IUseFormEmbeddedLayoutReturn, useFormEmbeddedLayout } from '@/modules/chat-matrix-fast/composables/useFormEmbeddedLayout'
import { type IUseResizablePaneReturn, useResizablePane } from '@/modules/chat-matrix-fast/composables/useResizablePane'

export interface IUseEmbeddedChatSetupParams {
  isEditMode: Ref<boolean> | ComputedRef<boolean>
  chat: Omit<IUseEntityChatParams, 'isEnabled'>
  chatEnabled?: Ref<boolean> | ComputedRef<boolean>
  storageKey: string
}

export interface IUseEmbeddedChatSetupReturn {
  formEmbeddedLayout: IUseFormEmbeddedLayoutReturn
  entityChat: IUseEntityChatReturn
  chatPaneResizer: IUseResizablePaneReturn
  contentClass: ComputedRef<(string | Record<string, boolean>)[] | undefined>
}

export function useEmbeddedChatSetup(params: IUseEmbeddedChatSetupParams): IUseEmbeddedChatSetupReturn {
  const formEmbeddedLayout = useFormEmbeddedLayout(params.isEditMode)

  const entityChat = useEntityChat({
    appLabel: params.chat.appLabel,
    modelName: params.chat.modelName,
    objectId: params.chat.objectId,
    isEnabled: params.chatEnabled ?? formEmbeddedLayout.isEmbedded,
  })

  const chatPaneResizer = useResizablePane({
    initial: Math.floor(window.innerWidth * 0.4),
    min: 500,
    max: () => Math.floor(window.innerWidth / 2),
    storageKey: params.storageKey,
  })

  // Базовый embedded-класс + `--no-chat`, когда чат скрыт; иначе undefined — дефолтные стили Vuetify.
  const contentClass = computed(() =>
    formEmbeddedLayout.contentClass.value
      ? [formEmbeddedLayout.contentClass.value, { 'entity-form-embedded--no-chat': !entityChat.isChatVisible.value }]
      : undefined,
  )

  return { formEmbeddedLayout, entityChat, chatPaneResizer, contentClass }
}
